import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Basis } from '~/models/studio';
import { clamp } from '~/util';
import { useRaf } from '~/util/animation';
import { decodeSimulatedData, ErrSimDataStale, measure } from '../simulation';
import type { ExperimentState } from '../experiment/ExperimentProvider';
import type {
  MeasuredComponent,
  SimResult,
  SimulatedComponent,
  SimulationState
} from '../simulation';
import type { ReactNode } from 'react';

export interface CurrentFrameState {
  simResult: SimResult | null;
  currentTime: number;
  playableEnd: number;
  animationSpeed: number;
  components: SimulatedComponent[];
  numAtoms: number;
  logErrorEst: number | null;
  averageState: AtomState[];
  animationRunning: boolean;
  setCurrentTime: (time: number) => void;
  setAnimationSpeed: (speed: number) => void;
  getMeasure: (basis: Basis) => MeasuredComponent[];
}

const CurrentFrameContext = createContext<CurrentFrameState | null>(null);
CurrentFrameContext.displayName = 'CurrentFrame';

export function CurrentFrameProvider({
  children,
  simulation,
  experiment
}: {
  children: ReactNode;
  simulation: SimulationState;
  experiment: ExperimentState;
}) {
  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [currentTime, setCurrentTimeState] = useState(0);
  const [animationSpeed, setAnimationSpeedState] = useState(0);

  const ANIMATION_TICKS_DONE_OFFSET = 1;

  const playableEnd = useMemo(
    () =>
      Math.max(
        simulation.progress.done
          ? experiment.maxTime
          : simulation.progress.ticksDone - ANIMATION_TICKS_DONE_OFFSET,
        0
      ),
    [
      experiment.maxTime,
      simulation.progress.done,
      simulation.progress.ticksDone
    ]
  );

  const getCurrentTime = useCallback((): number => {
    return clamp(Math.round(currentTime), 0, experiment.maxTime);
  }, [currentTime, experiment]);

  // Reset when experiment is replaced
  useEffect(() => {
    return experiment.onReplace(() => {
      setAnimationSpeedState(0);
      setCurrentTimeState(0);
    });
  }, [experiment]);

  // Fetch simulation data when time or sim nonce changes
  useEffect(() => {
    const done = simulation.progress.ticksDone;
    const current = getCurrentTime();
    if (done - 1 >= current) {
      const queryTime = Math.min(done - 1, current);

      simulation
        .requestData(queryTime)
        .then((res) => {
          setSimResult(res.result);
        })
        .catch((e) => {
          if (!(e instanceof ErrSimDataStale)) {
            throw e;
          }
        });
    } else {
      setSimResult(null);
    }
  }, [getCurrentTime, simulation, simulation.progress]);

  // Animation RAF
  useRaf(
    () => animationSpeed !== 0,
    useCallback(
      (_, dt) => {
        const newTime = clamp(
          currentTime + (animationSpeed * dt) / 10,
          0,
          playableEnd
        );

        const isForward = animationSpeed > 0;
        const shouldStop =
          (isForward && newTime >= playableEnd) || (!isForward && newTime <= 0);

        if (shouldStop) {
          setCurrentTimeState(isForward ? playableEnd : 0);
          setAnimationSpeedState(0);
        } else {
          setCurrentTimeState(newTime);
        }
      },
      [playableEnd, currentTime, animationSpeed]
    )
  );

  const components = useMemo((): SimulatedComponent[] => {
    return simResult !== null ? decodeSimulatedData(simResult) : [];
  }, [simResult]);

  const numAtoms = useMemo((): number => {
    return simResult?.indexSize ?? 0;
  }, [simResult]);

  const logErrorEst = useMemo((): number | null => {
    return simResult?.logErrorEst ?? null;
  }, [simResult]);

  const averageState = useMemo((): AtomState[] => {
    return getAtomStateFromComponents(components, numAtoms);
  }, [components, numAtoms]);

  const animationRunning = animationSpeed !== 0;

  const setAnimationSpeed = useCallback((speed: number) => {
    setAnimationSpeedState(speed);
  }, []);

  const setCurrentTime = useCallback((time: number) => {
    setAnimationSpeedState(0); // Stop animation when setting time manually
    setCurrentTimeState(Math.max(0, time));
  }, []);

  const getMeasure = useCallback(
    (basis: Basis): MeasuredComponent[] => {
      return components.length > 0 ? measure(components, basis) : [];
    },
    [components]
  );

  // Create reactive context value that changes when state changes
  const value: CurrentFrameState = useMemo(
    () => ({
      simResult,
      currentTime: getCurrentTime(),
      playableEnd,
      animationSpeed,
      components,
      numAtoms,
      logErrorEst,
      averageState,
      animationRunning,
      setCurrentTime,
      setAnimationSpeed,
      getMeasure
    }),
    [
      simResult,
      getCurrentTime,
      playableEnd,
      animationSpeed,
      components,
      numAtoms,
      logErrorEst,
      averageState,
      animationRunning,
      setCurrentTime,
      setAnimationSpeed,
      getMeasure
    ]
  );

  return (
    <CurrentFrameContext.Provider value={value}>
      {children}
    </CurrentFrameContext.Provider>
  );
}

export function useCurrentFrame(): CurrentFrameState {
  const ctx = useContext(CurrentFrameContext);
  if (!ctx) {
    throw new Error('CurrentFrame not provided');
  }
  return ctx;
}

/**
 * Probability of state, in order [g, h, r]
 */
export type AtomState = [number, number, number];

/**
 * Get average per-atom state from list of superposed quantum state components.
 *
 * This conversion is lossy, as entangled states are not separable in principle.
 * This process serves as a simple way to get a decent visualization of the averaged state.
 *
 * @returns set of probablitities for each atom that it occupies a specific energy level
 */
export function getAtomStateFromComponents(
  components: SimulatedComponent[] | MeasuredComponent[],
  numAtoms: number,
  basis?: Basis | null
): AtomState[] {
  const state: AtomState[] = Array.from({ length: numAtoms }, () => [0, 0, 0]);
  if (components.length === 0) {
    return state;
  }

  if ('qutritIndices' in components[0]) {
    for (const c of components as SimulatedComponent[]) {
      let index = c.qutritIndices;
      for (let i = 0; i < numAtoms; i++) {
        const q = Number(index & 3n);
        // when shifting index out to the right, the atoms are read from last to first
        state[numAtoms - i - 1][q] += c.mag2;
        index >>= 2n;
      }
    }
  } else if ('qubitIndices' in components[0] && basis != null) {
    for (const c of components as MeasuredComponent[]) {
      let index = c.qubitIndices;
      for (let i = 0; i < numAtoms; i++) {
        let q = index & 1;

        /**
         * Reconcile measurement with probable source state. We basically discard the
         * states that are not in the basis we measure in:
         * digital:
         * - 0 (r,g) -> 00 (g)
         * - 1 (h) -> 01 (h)
         * ground-rydberg:
         * - 0 (g,h) -> 00 (g)
         * - 1 (r) -> 10 (r)
         */
        if (basis === Basis.GroundRydberg && q === 1) {
          q = 2;
        }
        // when shifting index out to the right, the atoms are read from last to first
        state[numAtoms - i - 1][q] += c.mag2;
        index >>= 1;
      }
    }
  }
  return state;
}
